class HeaderInline extends HTMLElement {
  constructor() {
    super();
    this.isLogoReducedOnScroll = this.hasAttribute('data-logo-reduced-on-scroll');
    this.megaMenuUrl = this.dataset.megaMenuUrl;
    this.megaMenusRequest = null;
    this.megaMenuParentElements = this.querySelectorAll('[data-mega-menu-parent]');
    this.quickBuyButtonElements = this.querySelectorAll('[data-mega-menu-quick-buy]');
    this.showMegaMenu = this.showMegaMenu.bind(this);
    this.updateScrolledState = this.updateScrolledState.bind(this);
    this.addProductToCart = this.addProductToCart.bind(this);
  }

  connectedCallback() {
    this.megaMenuParentElements.forEach((megaMenuParentElement) => {
      megaMenuParentElement.addEventListener('mouseenter', this.showMegaMenu);
      megaMenuParentElement.addEventListener('focusin', this.showMegaMenu);
    });
    this.bindQuickBuyButtons();

    if (!this.isLogoReducedOnScroll) return;
    window.addEventListener('scroll', this.updateScrolledState, { passive: true });
    window.addEventListener('resize', this.updateScrolledState, { passive: true });
    this.updateScrolledState();
  }

  disconnectedCallback() {
    this.megaMenuParentElements.forEach((megaMenuParentElement) => {
      megaMenuParentElement.removeEventListener('mouseenter', this.showMegaMenu);
      megaMenuParentElement.removeEventListener('focusin', this.showMegaMenu);
    });
    this.quickBuyButtonElements.forEach((quickBuyButtonElement) => {
      quickBuyButtonElement.removeEventListener('click', this.addProductToCart);
    });
    window.removeEventListener('scroll', this.updateScrolledState);
    window.removeEventListener('resize', this.updateScrolledState);
  }

  updateScrolledState() {
    const isPageScrolled = window.scrollY > 200;
    const isHeaderAtViewportTop = Math.round(this.getBoundingClientRect().top) <= 0;
    this.classList.toggle('header-inline--scrolled', isPageScrolled && isHeaderAtViewportTop);
  }

  bindQuickBuyButtons() {
    this.quickBuyButtonElements = this.querySelectorAll('[data-mega-menu-quick-buy]');
    this.quickBuyButtonElements.forEach((quickBuyButtonElement) => {
      quickBuyButtonElement.addEventListener('click', this.addProductToCart);
    });
  }

  loadMegaMenus() {
    if (!this.megaMenuUrl) return Promise.resolve();

    if (!this.megaMenusRequest) {
      this.megaMenusRequest = fetch(this.megaMenuUrl)
        .then((response) => response.text())
        .then((responseText) => this.insertMegaMenus(responseText))
        .catch(() => {
          this.megaMenusRequest = null;
        });
    }

    return this.megaMenusRequest;
  }

  insertMegaMenus(responseText) {
    const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');

    this.megaMenuParentElements.forEach((megaMenuParentElement) => {
      const linkPosition = megaMenuParentElement.dataset.megaMenuParent;
      const megaMenuElement = responseDocument.querySelector(
        `[data-mega-menu-parent="${linkPosition}"] [data-mega-menu]`
      );
      const skeletonMegaMenuElement = megaMenuParentElement.querySelector('[data-mega-menu]');
      if (megaMenuElement && skeletonMegaMenuElement) skeletonMegaMenuElement.replaceWith(megaMenuElement);
    });

    this.megaMenuUrl = null;
    this.bindQuickBuyButtons();
  }

  async showMegaMenu(pointerOrFocusEvent) {
    const megaMenuParentElement = pointerOrFocusEvent.currentTarget;
    await this.loadMegaMenus();
    this.updateMegaMenuLayout(megaMenuParentElement);
  }

  updateMegaMenuLayout(megaMenuParentElement) {
    const megaMenuGroupsElement = megaMenuParentElement.querySelector('[data-mega-menu-groups]');
    if (!megaMenuGroupsElement) return;
    this.updateMegaMenuGroupsLayout(megaMenuGroupsElement);
    this.markLastGroupsInColumns(megaMenuGroupsElement);
  }

  updateMegaMenuGroupsLayout(megaMenuGroupsElement) {
    const fittedColumnsClass = 'header-inline__mega-menu-groups-wrapper--fitted-columns';
    megaMenuGroupsElement.classList.remove(fittedColumnsClass);

    const hasHorizontalScroll = megaMenuGroupsElement.scrollWidth > megaMenuGroupsElement.clientWidth;
    if (!hasHorizontalScroll) return;

    const megaMenuGroupElements = megaMenuGroupsElement.querySelectorAll('[data-mega-menu-group]');
    const groupWidth = megaMenuGroupElements[0].offsetWidth;
    const columnGap = parseFloat(getComputedStyle(megaMenuGroupsElement).columnGap) || 0;
    const fittedColumnsCount = Math.max(
      1,
      Math.floor((megaMenuGroupsElement.clientWidth + columnGap) / (groupWidth + columnGap))
    );

    megaMenuGroupsElement.style.setProperty('--columns-count', fittedColumnsCount);
    megaMenuGroupsElement.classList.add(fittedColumnsClass);
  }

  setQuickBuyLoadingState(quickBuyButtonElement, isLoading) {
    quickBuyButtonElement.disabled = isLoading;
    quickBuyButtonElement.setAttribute('aria-busy', isLoading);
  }

  async addProductToCart(clickEvent) {
    const quickBuyButtonElement = clickEvent.currentTarget;
    const variantId = quickBuyButtonElement.dataset.variantId;

    if (!variantId || quickBuyButtonElement.disabled) return;

    this.setQuickBuyLoadingState(quickBuyButtonElement, true);

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: 'cart_data,mini_cart',
          sections_url: window.location.pathname
        })
      });

      const cartData = await response.json();

      if (cartData.status) {
        document.dispatchEvent(new CustomEvent('header-inline:quick-buy-error', { detail: { error: cartData } }));
        return;
      }

      if (window.T4SThemeSP && window.T4SThemeSP.Cart) {
        window.T4SThemeSP.Cart.renderContents(cartData.sections, true);
      }

      document.dispatchEvent(new CustomEvent('header-inline:quick-buy-added', { detail: { cart: cartData } }));
    } catch (error) {
      document.dispatchEvent(new CustomEvent('header-inline:quick-buy-error', { detail: { error: error } }));
    } finally {
      this.setQuickBuyLoadingState(quickBuyButtonElement, false);
    }
  }

  markLastGroupsInColumns(megaMenuGroupsElement) {
    const megaMenuGroupElements = megaMenuGroupsElement.querySelectorAll('[data-mega-menu-group]');

    megaMenuGroupElements.forEach((megaMenuGroupElement, groupIndex) => {
      const nextGroupElement = megaMenuGroupElements[groupIndex + 1];
      const isLastInColumn = !nextGroupElement || nextGroupElement.offsetLeft !== megaMenuGroupElement.offsetLeft;
      megaMenuGroupElement.classList.toggle('header-inline__mega-menu-group--last-item', isLastInColumn);
    });
  }
}

if (!customElements.get('header-inline')) {
  customElements.define('header-inline', HeaderInline);
}
